import { citiesByProvince, cityBySlug } from "./cities";
import { services } from "./services";
import { hashStr, pick } from "../seo/content";

const qcCities = citiesByProvince("quebec");

export const FR_SLUGS = Object.fromEntries(qcCities.map((c) => [c.slug, `gardiens-de-securite-${c.slug}`]));

export const FR_SERVICE_SLUGS = {
  "uniformed-security-guards": "gardiens-de-securite-en-uniforme",
  "mobile-security-patrol": "patrouille-mobile-de-securite",
  "construction-site-security": "securite-de-chantiers-de-construction",
  "commercial-office-security": "securite-commerciale-et-de-bureaux",
  "retail-loss-prevention": "prevention-des-pertes-au-detail",
  "event-security": "securite-d-evenements",
  "concierge-front-desk-security": "securite-concierge-et-reception",
  "industrial-warehouse-security": "securite-industrielle-et-d-entrepots",
  "healthcare-facility-security": "securite-des-etablissements-de-sante",
  "residential-condo-security": "securite-residentielle-et-condos",
  "vip-executive-protection": "protection-rapprochee-vip",
  "cctv-remote-monitoring": "telesurveillance-24-7",
  "fire-watch-security": "surveillance-incendie",
  "traffic-control-parking-enforcement": "controle-de-la-circulation-et-stationnement",
  "alarm-response-keyholding": "reponse-aux-alarmes-et-garde-de-cles",
  "crisis-strike-security": "gestion-de-crise-et-securite-de-greve",
  "school-campus-security": "securite-des-ecoles-et-campus",
  "hotel-hospitality-security": "securite-hoteliere",
  "logistics-transport-escort": "escorte-logistique-et-transport",
  "security-risk-assessment": "evaluation-des-risques-de-securite",
};

export const frServiceNames = {
  "uniformed-security-guards": "Gardiens de sécurité en uniforme",
  "mobile-security-patrol": "Patrouille mobile de sécurité",
  "construction-site-security": "Sécurité de chantiers de construction",
  "commercial-office-security": "Sécurité commerciale et de bureaux",
  "retail-loss-prevention": "Prévention des pertes au détail",
  "event-security": "Sécurité d'événements",
  "concierge-front-desk-security": "Sécurité concierge et réception",
  "industrial-warehouse-security": "Sécurité industrielle et d'entrepôts",
  "healthcare-facility-security": "Sécurité des établissements de santé",
  "residential-condo-security": "Sécurité résidentielle et condos",
  "vip-executive-protection": "Protection rapprochée VIP et cadres",
  "cctv-remote-monitoring": "Télésurveillance 24/7 et vidéosurveillance",
  "fire-watch-security": "Surveillance incendie (fire watch)",
  "traffic-control-parking-enforcement": "Contrôle de la circulation et stationnement",
  "alarm-response-keyholding": "Réponse aux alarmes et garde de clés",
  "crisis-strike-security": "Gestion de crise et sécurité de grève",
  "school-campus-security": "Sécurité des écoles et campus",
  "hotel-hospitality-security": "Sécurité hôtelière et de l'accueil",
  "logistics-transport-escort": "Escorte logistique et transport",
  "security-risk-assessment": "Évaluation des risques de sécurité",
};

export const frServiceShorts = {
  "uniformed-security-guards": "Des gardiens en uniforme, professionnels et présents sur place pour les propriétés commerciales, résidentielles et publiques.",
  "mobile-security-patrol": "Des patrouilles en véhicule identifié qui inspectent vos sites selon des horaires aléatoires et vérifiés.",
  "construction-site-security": "La surveillance de chantiers actifs, de cours à matériaux et de projets contre le vol et le vandalisme.",
  "commercial-office-security": "Gardiens de hall d'entrée, contrôle d'accès et sécurité des locataires pour bureaux et tours commerciales.",
  "retail-loss-prevention": "Des spécialistes de la prévention des pertes, en civil ou en uniforme, pour commerces et centres d'achats.",
  "event-security": "Gestion des foules, contrôle des accès et gardiens licenciés pour des événements de toute envergure.",
  "concierge-front-desk-security": "Des gardiens formés à l'accueil qui combinent service aux résidents et protection de l'immeuble.",
  "industrial-warehouse-security": "Contrôle de guérite, surveillance de cour et protection des stocks pour entrepôts et usines.",
  "healthcare-facility-security": "Des gardiens formés à la désescalade pour hôpitaux, cliniques et centres de soins de longue durée.",
  "residential-condo-security": "Patrouilles de courtoisie, concierge et surveillance des aires communes pour copropriétés et résidences.",
  "vip-executive-protection": "Une protection rapprochée discrète pour cadres, personnalités publiques et visiteurs de marque.",
  "cctv-remote-monitoring": "Surveillance vidéo en direct et vérification d'alarmes par des opérateurs humains, jour et nuit.",
  "fire-watch-security": "Une surveillance incendie conforme au code lorsque vos systèmes d'alarme ou de gicleurs sont hors service.",
  "traffic-control-parking-enforcement": "Signaleurs certifiés, contrôle du stationnement et gestion de la circulation.",
  "alarm-response-keyholding": "Réponse rapide aux alarmes avec garde de clés sécurisée et présence sur site.",
  "crisis-strike-security": "Une couverture de sécurité calme et conforme pour conflits de travail, urgences et interruptions.",
  "school-campus-security": "Des gardiens axés sur la protection des jeunes pour écoles, collèges et campus universitaires.",
  "hotel-hospitality-security": "Une sécurité axée sur l'expérience client pour hôtels, centres de villégiature et restaurants.",
  "logistics-transport-escort": "Escorte et protection de la chaîne d'approvisionnement pour le fret de grande valeur.",
  "security-risk-assessment": "Des audits professionnels qui identifient les vulnérabilités avant qu'elles ne deviennent des incidents.",
};

export const frServiceDeliverables = {
  "uniformed-security-guards": [
    "Postes fixes pour halls d'entrée, barrières et périmètres",
    "Patrouilles à pied avec points de contrôle numériques",
    "Contrôle d'accès et gestion des visiteurs",
    "Rapports d'incident horodatés quotidiens",
    "Intervention d'urgence et personnel certifié en premiers soins",
    "Relèves de quart détaillées et audits de supervision",
  ],
  "mobile-security-patrol": [
    "Routes de patrouille aléatoires et planifiées",
    "Points de contrôle vérifiés par GPS à chaque visite",
    "Vérification des serrures, barrières et périmètres",
    "Réponse rapide aux alarmes et incidents",
    "Rapport photographique après chaque patrouille",
    "Surveillance de propriétés vacantes et cours de chantier",
  ],
  "construction-site-security": [
    "Surveillance de chantier de nuit et de fin de semaine",
    "Contrôle des livraisons et vérification des conducteurs",
    "Vérification des inventaires d'équipement et matériaux",
    "Dissuasion et protocoles d'expulsion des intrus",
    "Surveillance incendie pendant les travaux à chaud",
    "Rapports quotidiens sur l'état du site et les dangers",
  ],
  "commercial-office-security": [
    "Postes de sécurité en hall d'entrée et à la réception",
    "Systèmes de cartes d'accès et registre des visiteurs",
    "Escorte des locataires et fermeture après les heures",
    "Surveillance des quais de chargement et salles de courrier",
    "Guides-étages pour évacuations d'urgence",
    "Rapports d'incident pour la gestion immobilière",
  ],
  "retail-loss-prevention": [
    "Agents de prévention des pertes en civil",
    "Dissuasion en uniforme aux entrées et sorties",
    "Interventions de vol à l'étalage dans le respect de la loi",
    "Surveillance du plancher assistée par vidéo",
    "Soutien aux enquêtes sur le vol interne",
    "Rapports et analyses des tendances de démarque",
  ],
  "event-security": [
    "Gestion des foules et des files d'attente",
    "Vérification des billets et accréditations",
    "Fouilles de sacs et détection de métaux",
    "Protection des coulisses et zones VIP",
    "Gardiens licenciés pour événements spéciaux",
    "Compte rendu post-événement et registre d'incidents",
  ],
  "concierge-front-desk-security": [
    "Service à la réception et aide aux résidents",
    "Gestion des colis et des livraisons",
    "Filtrage des visiteurs et contrôle des clés",
    "Coordination des réservations et déménagements",
    "Surveillance des caméras et panneaux d'alarme",
    "Patrouilles nocturnes et vérification des serrures",
  ],
  "industrial-warehouse-security": [
    "Contrôle d'accès en guérite et au pèse-essieux",
    "Vérification des scellés de remorques et conteneurs",
    "Patrouilles de cour et inspections de clôtures",
    "Supervision des expéditions et réceptions",
    "Observation des dangers et rapports de sécurité",
    "Couverture de nuit et de jours fériés",
  ],
  "healthcare-facility-security": [
    "Présence à l'urgence et désescalade",
    "Surveillance de patients et prévention des fugues",
    "Patrouilles des stationnements et extérieurs",
    "Contrôle d'accès aux unités restreintes",
    "Soutien aux codes blancs et situations d'urgence",
    "Documentation d'incidents pour les autorités de santé",
  ],
  "residential-condo-security": [
    "Patrouilles nocturnes des aires communes et stationnements",
    "Concierge et gestion des visiteurs",
    "Surveillance des aires de loisirs et piscines",
    "Réponse aux plaintes de bruit et règlements",
    "Sécurité des salles de colis",
    "Rapports pour syndicats et gestionnaires",
  ],
  "vip-executive-protection": [
    "Agents de protection rapprochée",
    "Évaluation préalable des lieux et itinéraires",
    "Coordination du transport sécurisé",
    "Gestion des arrivées aux événements et lieux",
    "Analyses de risque pour les déplacements",
    "Opérations confidentielles et discrètes",
  ],
  "cctv-remote-monitoring": [
    "Surveillance vidéo en direct par opérateurs formés",
    "Vérification d'alarmes et répartition",
    "Rondes de garde virtuelles planifiées",
    "Intervention audio bidirectionnelle et avertissements",
    "Clips d'incidents et préparation de preuves",
    "Vérifications des systèmes et rapports de disponibilité",
  ],
  "fire-watch-security": [
    "Patrouilles continues des zones touchées",
    "Registres de surveillance incendie horodatés pour les autorités",
    "Supervision des travaux à chaud",
    "Protocoles de notification d'urgence immédiate",
    "Coordination avec les pompiers et inspecteurs",
    "Documentation conforme aux exigences d'assurance",
  ],
  "traffic-control-parking-enforcement": [
    "Signaleurs routiers certifiés",
    "Contrôle du stationnement et constats",
    "Acheminement des véhicules d'événements et chantiers",
    "Gestion des fermetures de route et détours",
    "Planification des quais de livraison",
    "Registres quotidiens de circulation et d'incidents",
  ],
  "alarm-response-keyholding": [
    "Répartition 24/7 de réponse aux alarmes",
    "Garde de clés sécurisée avec contrôle audité",
    "Vérifications intérieures et extérieures des lieux",
    "Réinitialisation des fausses alarmes et coordination",
    "Services de verrouillage et déverrouillage",
    "Rapport de délai de réponse pour chaque appel",
  ],
  "crisis-strike-security": [
    "Surveillance des piquets de grève et des accès",
    "Registres vidéo et d'incidents à valeur probante",
    "Services d'escorte pour personnel et direction",
    "Contrôle d'accès pendant les arrêts de travail",
    "Protection des sites en continuité des activités",
    "Liaison avec la police et les conseillers juridiques",
  ],
  "school-campus-security": [
    "Surveillance du périmètre et des entrées",
    "Gestion des visiteurs et registre",
    "Couverture d'événements et périodes d'examens",
    "Patrouilles des résidences et stationnements",
    "Soutien aux confinements d'urgence",
    "Personnel formé à la protection et vérifié",
  ],
  "hotel-hospitality-security": [
    "Patrouilles du hall et des étages",
    "Sécurité des événements et banquets",
    "Intervention et aide aux clients",
    "Surveillance des caméras et cartes d'accès",
    "Portiers pour bars et établissements licenciés",
    "Gestion des objets perdus et des preuves",
  ],
  "logistics-transport-escort": [
    "Escortes de chargements de grande valeur",
    "Sécurité de convoi du départ à destination",
    "Suivi des conducteurs et des itinéraires",
    "Protection des quais de transbordement",
    "Documentation de la chaîne de traçabilité",
    "Protocoles d'intervention en cas de vol",
  ],
  "security-risk-assessment": [
    "Audits de vulnérabilité et de menaces sur site",
    "Revue de l'éclairage, des clôtures et des lignes de vue",
    "Analyse des lacunes de caméras et d'alarmes",
    "Rapport de risque écrit avec correctifs priorisés",
    "Conception de programmes de sécurité et consignes",
    "Documentation alignée sur les exigences d'assurance",
  ],
};

const NAME_FR = { montreal: "Montréal", "quebec-city": "Ville de Québec" };
export const frCityName = (slug) => NAME_FR[slug] || cityBySlug[slug]?.name || slug;
const frRegion = (c) => (c.region === "Quebec" ? "les environs" : c.region);

const frCityIntros = [
  (c, n) =>
    `${n} compte des entreprises, des chantiers et des institutions qui méritent une protection fiable, jour et nuit. Maze Security Solutions fournit des gardiens de sécurité licenciés partout dans ${frRegion(c)}, appuyés par une répartition 24/7 et des rapports d'incident horodatés. Postes fixes, patrouilles mobiles ou programmes multisites : chaque mandat est construit selon un même standard — une protection que vous pouvez vérifier.`,
  (c, n) =>
    `Les gestionnaires immobiliers et les entreprises de ${frRegion(c)} choisissent Maze Security Solutions pour une raison simple : nous arrivons à l'heure, avec des gardiens licenciés qui connaissent ${n}. Commerce de détail, chantier de construction, tour résidentielle ou cour industrielle — nos programmes combinent personnel formé, patrouilles vérifiées et rapports clairs.`,
  (c, n) =>
    `La sécurité à ${n} n'est pas un produit de base — c'est un engagement envers les gens et les propriétés qui font vivre la région. Maze Security Solutions déploie des gardiens licenciés partout dans ${frRegion(c)} pour les environnements commerciaux, résidentiels, industriels et événementiels, avec répartition en direct et rapports numériques.`,
  (c, n) =>
    `Vol, intrusion, vandalisme, responsabilité civile : les propriétés de ${n} font face à des risques que les caméras seules ne règlent pas. Maze Security Solutions offre des services professionnels de gardiens de sécurité partout dans ${frRegion(c)} — gardiens en uniforme, patrouilles mobiles, concierge et réponse rapide aux alarmes — sous une structure de commandement unique et imputable.`,
];

const frCityContext = [
  (c, n) =>
    `Au Québec, la sécurité privée est encadrée par la Loi sur la sécurité privée et le Bureau de la sécurité privée (BSP). Chaque gardien déployé à ${n} détient un permis d'agent valide et a complété la formation obligatoire de 70 heures avant son premier quart. Nos superviseurs effectuent des visites surprises et chaque patrouille est vérifiée par des points de contrôle numériques.`,
  (c, n) =>
    `Chaque mandat à ${n} commence par une visite de votre propriété et une discussion honnête sur les risques. Tous nos gardiens détiennent un permis valide du BSP, conformément à la Loi sur la sécurité privée, et nos clients reçoivent les numéros de permis sur demande avant le premier quart de travail.`,
  (c, n) =>
    `Opérer au Québec signifie respecter la Loi sur la sécurité privée. Nos gardiens à ${n} détiennent un permis d'agent du BSP, ont complété la formation de 70 heures et travaillent sous la supervision de chefs d'équipe qui vérifient chaque quart sans préavis.`,
];

const frCityCtas = [
  (c, n) =>
    `Besoin de gardiens à ${n} rapidement? Notre ligne de répartition répond jour et nuit. Composez le +1 807 907-2874 ou écrivez à info@mazesecurity.ca — un coordonnateur évaluera vos besoins le jour même, sans formulaire à remplir.`,
  (c, n) =>
    `Qu'il s'agisse d'un seul gardien de nuit ou d'un programme complet à ${n}, la prochaine étape est un simple appel : +1 807 907-2874, ou un courriel à info@mazesecurity.ca.`,
  (c, n) =>
    `Maze Security Solutions peut déployer des gardiens licenciés sur votre propriété de ${n} rapidement, avec des rapports vérifiables dès le premier jour. Appelez le +1 807 907-2874 ou écrivez à info@mazesecurity.ca.`,
];

const frCityFaqsBuild = (n, region) => [
  {
    q: `Vos gardiens à ${n} sont-ils licenciés?`,
    a: `Oui. Chaque gardien déployé à ${n} détient un permis d'agent valide émis par le Bureau de la sécurité privée (BSP), conformément à la Loi sur la sécurité privée du Québec, et a complété la formation obligatoire de 70 heures avant son premier quart.`,
  },
  {
    q: `En combien de temps pouvez-vous déployer des gardiens à ${n}?`,
    a: `Notre répartition 24/7 coordonne les déploiements dans ${region}. Les contrats standard débutent par une visite des lieux et des consignes écrites; les couvertures urgentes comme la surveillance incendie peuvent souvent être organisées le jour même. Composez le +1 807 907-2874.`,
  },
  {
    q: `Quels services de sécurité offrez-vous à ${n}?`,
    a: `Notre gamme complète : gardiens en uniforme, patrouille mobile, sécurité de chantiers, sécurité d'événements, concierge et réception, prévention des pertes au détail, surveillance incendie, réponse aux alarmes et télésurveillance. Chaque programme est adapté à votre site après une visite des lieux.`,
  },
  {
    q: "Comment puis-je vérifier que les patrouilles ont réellement eu lieu?",
    a: `Chaque patrouille et chaque quart à ${n} est vérifié par des points de contrôle géolocalisés et des rapports numériques horodatés. Vous recevez une preuve documentée de la couverture — pas de simples promesses.`,
  },
];

const overrides = {
  montreal: {
    description: "Gardiens de sécurité licenciés à Montréal : patrouille mobile, sécurité d'événements, chantiers et concierge. Répartition 24/7 : +1 807 907-2874.",
    lead: "Gardiens licenciés, patrouilles mobiles, sécurité d'événements et réponse aux alarmes 24/7 dans tout le Grand Montréal — une couverture vérifiable par Maze Security Solutions.",
    paras: [
      "Montréal bouge jour et nuit — chantiers, commerces, tours de bureaux et grands événements animent la métropole en tout temps. Maze Security Solutions fournit des gardiens de sécurité licenciés partout dans le Grand Montréal, appuyés par une répartition 24/7 et des rapports d'incident horodatés. Que vous gériez un immeuble résidentiel, un chantier de construction ou un commerce de détail, nos programmes de sécurité sont conçus pour les réalités montréalaises.",
      "Au Québec, la sécurité privée est encadrée par la Loi sur la sécurité privée et le Bureau de la sécurité privée (BSP). Chaque gardien déployé par Maze Security à Montréal détient un permis d'agent valide du BSP et a complété la formation obligatoire de 70 heures avant son premier quart de travail. Nos superviseurs effectuent des visites surprises et chaque patrouille est vérifiée par des points de contrôle numériques géolocalisés.",
      "Besoin de gardiens à Montréal rapidement? Notre ligne de répartition répond jour et nuit. Composez le +1 807 907-2874 ou écrivez à info@mazesecurity.ca — un coordonnateur évaluera vos besoins le jour même, sans formulaire à remplir.",
    ],
  },
  laval: {
    description: "Gardiens de sécurité licenciés à Laval : patrouille mobile, sécurité commerciale, industrielle et résidentielle. Répartition 24/7 : +1 807 907-2874.",
    lead: "Gardiens licenciés, patrouilles vérifiées et répartition 24/7 pour les parcs industriels, commerces et résidences de Laval et de la Rive-Nord.",
    paras: [
      "De Chomedey à Vimont, les parcs industriels, centres commerciaux et développements résidentiels de Laval exigent une présence de sécurité fiable et constante. Maze Security Solutions déploie des gardiens licenciés partout à Laval — postes fixes, patrouilles mobiles en véhicule identifié et réponse rapide aux alarmes — avec des rapports numériques horodatés après chaque quart de travail.",
      "Tous nos gardiens lavallois détiennent un permis d'agent valide du Bureau de la sécurité privée (BSP) et ont complété la formation de 70 heures exigée par la Loi sur la sécurité privée. Avant le premier quart, nous visitons votre site et rédigeons des consignes écrites; pendant le contrat, nos superviseurs vérifient la couverture sans préavis.",
      "Un besoin urgent à Laval — surveillance incendie, réponse à une alarme, couverture de chantier? Composez le +1 807 907-2874 ou écrivez à info@mazesecurity.ca. Notre répartition répond 24 heures sur 24, 7 jours sur 7.",
    ],
  },
  "quebec-city": {
    description: "Gardiens de sécurité licenciés à la Ville de Québec : événements, commerces, chantiers et concierge. Répartition 24/7 : +1 807 907-2874.",
    lead: "Gardiens licenciés et patrouilles vérifiées dans toute la Capitale-Nationale — du Vieux-Québec aux parcs industriels, avec répartition 24/7.",
    paras: [
      "Capitale nationale, pôle gouvernemental et destination touristique d'envergure, la Ville de Québec présente des besoins de sécurité aussi variés que ses quartiers : événements majeurs, commerces du Vieux-Québec, édifices de bureaux, chantiers et résidences. Maze Security Solutions y déploie des gardiens licenciés, des patrouilles mobiles vérifiées et des équipes de sécurité d'événements expérimentées.",
      "Conformément à la Loi sur la sécurité privée, chaque gardien que nous affectons à Québec détient un permis d'agent valide du Bureau de la sécurité privée (BSP) et a complété la formation obligatoire de 70 heures. Nos clients reçoivent les numéros de permis sur demande, des consignes écrites avant le premier quart et des rapports d'incident horodatés après chaque service.",
      "Pour discuter de vos besoins en sécurité à Québec, appelez notre répartition 24/7 au +1 807 907-2874 ou écrivez à info@mazesecurity.ca. Un coordonnateur vous répond le jour même — sans formulaire, sans détour.",
    ],
  },
};

export const frCities = qcCities.map((c) => {
  const name = frCityName(c.slug);
  const o = overrides[c.slug] || {};
  return {
    slug: FR_SLUGS[c.slug],
    citySlug: c.slug,
    name,
    title: `Gardiens de sécurité à ${name}, QC | Maze Security Solutions`,
    description: o.description || `Gardiens de sécurité licenciés à ${name} : patrouille mobile, événements, chantiers et concierge. Répartition 24/7 : +1 807 907-2874.`,
    overline: `Agence de sécurité · ${name}, QC`,
    h1: `Services de gardiens de sécurité à ${name}`,
    lead: o.lead || `Gardiens licenciés, patrouilles vérifiées et répartition 24/7 pour les entreprises et propriétés de ${name} — une couverture documentée par Maze Security Solutions.`,
    paras:
      o.paras || [
        pick(hashStr(c.slug), frCityIntros)(c, name),
        pick(hashStr(c.slug + "ctx"), frCityContext)(c, name),
        pick(hashStr(c.slug + "cta"), frCityCtas)(c, name),
      ],
    faqs: frCityFaqsBuild(name, frRegion(c)),
  };
});

export const frCityBySlug = Object.fromEntries(frCities.map((c) => [c.slug, c]));

const frServiceCityIntros = [
  (sn, c, n) =>
    `À ${n}, Maze Security Solutions offre ce service avec des gardiens licenciés par le Bureau de la sécurité privée (BSP), une supervision sur le terrain et des rapports vérifiables grâce à des points de contrôle numériques géolocalisés, partout dans ${frRegion(c)}.`,
  (sn, c, n) =>
    `Vous cherchez une équipe fiable à ${n}? Maze Security Solutions dessert ${frRegion(c)} avec des gardiens licenciés au Québec, une répartition 24/7 et des rapports d'incident horodatés que vous pouvez consulter en tout temps.`,
  (sn, c, n) =>
    `À ${n}, ce service exige plus qu'une simple présence. Maze Security déploie des gardiens détenant un permis valide du BSP, soutenus par des superviseurs et une répartition en direct, pour les clients de ${frRegion(c)}.`,
];

const frServiceCityLocal = [
  (c, n) =>
    `Chaque mandat à ${n} débute par une visite des lieux et des consignes écrites. Au Québec, la sécurité privée est encadrée par la Loi sur la sécurité privée : chaque gardien détient un permis d'agent valide du BSP et a complété la formation obligatoire de 70 heures. Les superviseurs vérifient les quarts sans préavis.`,
  (c, n) =>
    `Pour les clients de ${n}, cela signifie une sécurité vérifiable : numéros de permis sur demande, registres de patrouille géolocalisés et rapports d'incident dans votre boîte courriel avant le jour ouvrable suivant. Tous nos gardiens détiennent un permis valide du Bureau de la sécurité privée.`,
];

export const frServiceCityParas = (service, city) => {
  const n = frCityName(city.slug);
  return [
    pick(hashStr(service.slug + city.slug), frServiceCityIntros)(frServiceNames[service.slug], city, n),
    pick(hashStr(city.slug + service.slug + "l"), frServiceCityLocal)(city, n),
    pick(hashStr(city.slug + "cta"), frCityCtas)(city, n),
  ];
};

export const frServiceCityFaqs = (service, city) => {
  const sn = frServiceNames[service.slug];
  const n = frCityName(city.slug);
  return [
    {
      q: `Offrez-vous le service « ${sn} » à ${n}?`,
      a: `Oui. Maze Security Solutions offre ce service partout dans ${frRegion(city)}, avec des gardiens détenant un permis valide du Bureau de la sécurité privée (BSP), conformément à la Loi sur la sécurité privée du Québec.`,
    },
    {
      q: `Comment la couverture est-elle vérifiée à ${n}?`,
      a: "Chaque quart comprend une supervision sur le terrain, des points de contrôle géolocalisés et des rapports numériques horodatés envoyés directement à votre boîte courriel.",
    },
    {
      q: `En combien de temps pouvez-vous commencer à ${n}?`,
      a: "Les couvertures urgentes peuvent souvent être organisées le jour même via notre répartition 24/7. Les contrats standard débutent par une visite des lieux et des consignes écrites. Composez le +1 807 907-2874 ou écrivez à info@mazesecurity.ca.",
    },
  ];
};

export function resolveFrSlug(slug) {
  if (frCityBySlug[slug]) return { kind: "city", frCity: frCityBySlug[slug] };
  for (const s of services) {
    const prefix = `${FR_SERVICE_SLUGS[s.slug]}-a-`;
    if (slug.startsWith(prefix)) {
      const city = cityBySlug[slug.slice(prefix.length)];
      if (city && city.province === "quebec") return { kind: "serviceCity", service: s, city };
      return null;
    }
  }
  return null;
}

export const frLabels = {
  localServices: "Services de sécurité offerts",
  faqHeading: "Questions fréquentes",
  nearby: "Autres villes desservies au Québec",
  ctaTitle: (name) => `Des gardiens sur votre propriété à ${name}?`,
  ctaSub: "Appelez notre répartition 24/7 ou écrivez-nous. Les couvertures urgentes — surveillance incendie, réponse aux alarmes — peuvent souvent commencer le jour même.",
  breadcrumbHome: "Accueil",
  breadcrumbAreas: "Régions desservies",
  answers: "Réponses",
  scope: "Étendue des travaux",
  included: (s, c) => `${s} à ${c} — ce qui est inclus`,
  moreInCity: "Autres services",
  otherServicesTitle: (c) => `Autres services de sécurité à ${c}`,
  nearbyTitle: (s) => `${s} dans d'autres villes du Québec`,
  ctaServiceTitle: (s, c) => `${s} à ${c} : parlons-en`,
  ctaServiceSub: "Appelez notre répartition 24/7 ou écrivez-nous. Un coordonnateur visitera votre site et préparera des consignes écrites avant le premier quart.",
};

export const FR_SLUG_TO_EN = Object.fromEntries(Object.entries(FR_SERVICE_SLUGS).map(([en, fr]) => [fr, en]));

export const frServiceFaqs = (service) => {
  const sn = frServiceNames[service.slug];
  const d = frServiceDeliverables[service.slug];
  const low = (s) => s.charAt(0).toLowerCase() + s.slice(1);
  return [
    {
      q: `Que comprend le service « ${sn} »?`,
      a: `Ce programme comprend notamment : ${low(d[0])}, ${low(d[1])} et ${low(d[3])} — le tout documenté par des rapports numériques horodatés.`,
    },
    {
      q: "Vos gardiens sont-ils licenciés et assurés?",
      a: "Oui. Tout le personnel de Maze Security détient un permis de sécurité provincial ou territorial valide, a passé une vérification des antécédents et est couvert par notre assurance responsabilité civile. Des attestations sont disponibles sur demande.",
    },
    {
      q: "Dans quelles villes offrez-vous ce service?",
      a: "Nous offrons ce service dans plus de 690 villes canadiennes, dont 99 villes du Québec comme Montréal, Laval, la Ville de Québec, Sherbrooke et Saguenay.",
    },
    {
      q: "Comment démarrer?",
      a: "Appelez notre répartition 24/7 au +1 807 907-2874 ou écrivez à info@mazesecurity.ca. Un coordonnateur discutera de votre site, organisera une visite des lieux et préparera des consignes écrites avant le premier quart.",
    },
  ];
};

const frServiceIntros = [
  (service) =>
    `${frServiceShorts[service.slug]} Maze Security Solutions offre ce service dans les 13 provinces et territoires canadiens, avec du personnel licencié, une répartition 24/7 et des rapports numériques pour chaque mandat.`,
  (service) =>
    `${frServiceShorts[service.slug]} Notre programme combine des gardiens détenant un permis provincial valide, une supervision sur le terrain et une documentation vérifiable — partout au Canada.`,
];

const frServiceProcess = [
  "Chaque mandat débute par une visite des lieux et des consignes écrites, se poursuit avec des quarts supervisés et des points de contrôle vérifiés, et se termine par des rapports horodatés que vous pouvez consulter en tout temps.",
  "Notre approche est simple : visite des lieux, consignes écrites, gardiens licenciés, supervision sans préavis et rapports numériques après chaque quart. Vous savez toujours qui était sur place, quand, et ce qui a été fait.",
];

export const frServiceParas = (service) => [
  pick(hashStr(service.slug + "fr"), frServiceIntros)(service),
  pick(hashStr(service.slug + "frp"), frServiceProcess),
];

export const frHomeFaqs = [
  {
    q: "Quelles régions du Canada desservez-vous?",
    a: "Maze Security Solutions dessert les 13 provinces et territoires canadiens — Ontario, Québec, Colombie-Britannique, Alberta, Manitoba, Saskatchewan, Nouvelle-Écosse, Nouveau-Brunswick, Terre-Neuve-et-Labrador, Île-du-Prince-Édouard, Territoires du Nord-Ouest, Yukon et Nunavut — dans plus de 690 villes et communautés.",
  },
  {
    q: "Vos gardiens de sécurité sont-ils licenciés?",
    a: "Oui. Chaque gardien détient un permis provincial ou territorial valide — par exemple le permis d'agent du Bureau de la sécurité privée (BSP) au Québec ou la licence PSISA en Ontario — et a passé une vérification des antécédents avant son premier quart.",
  },
  {
    q: "Quels services Maze Security offre-t-elle?",
    a: "Nous offrons 20 services professionnels : gardiens en uniforme, patrouille mobile, sécurité de chantiers, sécurité d'événements, concierge et réception, prévention des pertes au détail, surveillance incendie, réponse aux alarmes et garde de clés, télésurveillance, protection rapprochée et évaluations des risques.",
  },
  {
    q: "Comment demander des services de sécurité?",
    a: "Appelez notre répartition 24/7 au +1 807 907-2874 ou écrivez à info@mazesecurity.ca. Un coordonnateur discutera de vos besoins, organisera une visite des lieux et préparera des consignes écrites — sans formulaire, sans attente.",
  },
  {
    q: "Comment la couverture des gardiens est-elle vérifiée?",
    a: "Tous les quarts et patrouilles sont supervisés et vérifiés par des points de contrôle géolocalisés et des rapports d'incident numériques horodatés. Vous recevez une preuve documentée de la couverture après chaque quart.",
  },
];
